import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { getItemsRepo } from "../repository/items_repository";
import { ItemFilterData, ItemModelV2 } from "../../models/item_model";
import ItemDescriptionAutoCompleteField from "./item_description_autocomplete";
import "../../../../ui/new_components/common/autocomplete/index.css";

const defaultFilter: ItemFilterData = {
  page: 1,
  count: 10,
};

interface ItemNameSearchWidgetProps {
  type?: string;
  value?: string;
  onSelect: (data: any) => void;
  placeholder?: string;
  emp?: { uid: string; name: string };
  disabled?: boolean;
  withBtn?: boolean;
  id?: string;
}

const ItemDescriptionSearchWidget: FunctionComponent<ItemNameSearchWidgetProps> = ({
  type,
  value,
  onSelect,
  placeholder,
  disabled,
  id,
}) => {
  const { showToast } = useToast();
  const [edit, setEdit] = useState(false);
  const [filter, setFilter] = useState<ItemFilterData>(defaultFilter);
  const [items, setItems] = useState<ItemModelV2[]>([]);
  const [loading, setLoading] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  const debouncedHandler = useCallback(
    debounce((query) => {
      if (query) searchItems(query);
    }, 500),
    [filter]
  );

  useEffect(() => {
    if (filter && edit) {
      debouncedHandler(filter);
    }
  }, [filter, debouncedHandler, edit]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        widgetRef.current &&
        !widgetRef.current.contains(event.target as Node)
      ) {
        setEdit(false);
      }
    };

    if (edit) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [edit]);

  const searchItems = async (search: string) => {
    setLoading(true);
    filter.type = type;
    const res = await getItemsRepo(filter);
    if (res.success) {
      setItems(res.data.data);
    } else {
      setItems([]);
      showToast({ type: ToastType.error, text: res.error ?? "" });
    }
    setLoading(false);
  };

  return (
    <div className="flex gap-1 items-center w-full" ref={widgetRef}>
      {edit ? (
        <ItemDescriptionAutoCompleteField
          loading={loading}
          value={value}
          placeholder={placeholder ?? "Description"}
          disabled={disabled}
          onSelect={(val) => {
            setEdit(false);
            // onSelect(val.value);
          }}
          suggestions={items.map((e) => ({
            label: (
              <div className="text-xs">
                <b>{e.description}</b>
              </div>
            ),
            value: e,
          }))}
          onChange={(str) => {
            setFilter((o) => ({
              ...o,
              description: str,
            }));
            onSelect(str);
          }}
        />
      ) : (
        <textarea
          id={id}
          className="text-sm px-3 py-2 h-24 rounded focus:outline-none w-full border"
          onClick={() => {
            if (!disabled && !edit) {
              setEdit(true);
            }
          }}
          value={value || placeholder}
          readOnly={disabled}
        />
      )}
    </div>
  );
};

export default ItemDescriptionSearchWidget;
